import {
  RADAR_AXIS_DISPLAY_NAMES,
  SCORING_TYPE,
  type AthleteRadarProfile,
  type AxisDetail,
  type BenchmarkStandards,
  type MovementRegistryEntry,
  type OneRepMax,
  type RadarAxis,
} from "@lemonadetraining/contracts";
import {
  DEFAULT_VISIBLE_LEVELS,
  defaultContextMessage,
  type AxisDetailIndicatorWithMeasure,
} from "./axisDetailHelpers";

/**
 * Per-axis detail computation for the web dashboard.
 *
 * The radar trigger (Cloud Functions `radarScoring`) is the source of truth
 * for `AthleteRadarProfile.scores`. This module decomposes that score into
 * per-movement indicators for the AxisDetail UI by mirroring the locked V1
 * scoring math from `features/next-gen-athlete-mvp/SCORING-V1.md`.
 *
 * Why client-side: the inputs (profile, demographics, 1RM records, registry)
 * are already streamed to the dashboard via Redux + Firestore listeners;
 * decomposing them into UI rows is a display transform on private data, not
 * a privileged computation.
 */

type Demographics = {
  gender?: "male" | "female" | "other" | null;
  bodyWeight?: number | null;
};

// Records carry an `unlocked` flag for binary movements that isn't yet on the
// published `OneRepMax` type. Narrow the cast to one place.
type RecordWithUnlock = OneRepMax & { unlocked?: boolean };
function isUnlocked(rec: OneRepMax): boolean {
  return (rec as RecordWithUnlock).unlocked === true;
}

/**
 * Classify bodyweight into V1 weight bands. Returns null when missing/invalid
 * so the multiplier resolution can fall back to a neutral 1.0.
 */
export function weightClassOf(bodyWeight: number | null | undefined): string | null {
  if (typeof bodyWeight !== "number" || !Number.isFinite(bodyWeight) || bodyWeight <= 0) {
    return null;
  }
  if (bodyWeight <= 60) return "≤60";
  if (bodyWeight <= 70) return "61-70";
  if (bodyWeight <= 80) return "71-80";
  if (bodyWeight <= 90) return "81-90";
  if (bodyWeight <= 100) return "91-100";
  return "100+";
}

/** SCORING-V1 §4.5 — human-readable benchmark label. */
export function computeBenchmarkLabel(profile: Demographics): string {
  const genderLabel = genderDisplay(profile.gender);
  const weightClass = weightClassOf(profile.bodyWeight);
  // Spec uses an en-dash for the weight-class range (e.g., "71–80 kg"). The
  // raw class string keeps an ASCII hyphen because it doubles as the Firestore
  // modifier key — substitute display-side only.
  const formatted = weightClass ? formatWeightClassForDisplay(weightClass) : null;
  if (genderLabel && formatted) return `${genderLabel}, ${formatted} kg`;
  if (genderLabel) return `${genderLabel}, all weights`;
  if (formatted) return `All genders, ${formatted} kg`;
  return "General adult";
}

function formatWeightClassForDisplay(weightClass: string): string {
  return weightClass.replace(/-/g, "–");
}

function genderDisplay(gender: Demographics["gender"]): string | null {
  if (gender === "male") return "Male";
  if (gender === "female") return "Female";
  return null;
}

function lookupMultiplier(dict: Record<string, number> | undefined, key: string | null): number {
  if (!dict || !key) return 1.0;
  const v = dict[key];
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 1.0;
}

function combinedMultiplier(
  modifiers: BenchmarkStandards["modifiers"],
  profile: Demographics
): number {
  const gender = profile.gender ?? null;
  const weightClass = weightClassOf(profile.bodyWeight);
  const g = lookupMultiplier(modifiers.gender, typeof gender === "string" ? gender : null);
  const w = lookupMultiplier(modifiers.weight, weightClass);
  return g * w;
}

/** Convert the raw `base` map into ascending [score, target] pairs. */
function toLevelEntries(base: BenchmarkStandards["base"]): Array<[number, number]> {
  return Object.entries(base ?? {})
    .map(([k, v]) => [Number(k), Number(v)] as [number, number])
    .filter(([k, v]) => Number.isFinite(k) && Number.isFinite(v))
    .sort((a, b) => a[0] - b[0]);
}

/**
 * Piecewise-linear interpolation between [score, target] pairs.
 * `reversed=true` for time-based scoring (lower value = higher score).
 */
function interpolate(value: number, targets: Array<[number, number]>, reversed = false): number {
  // Flip the target axis so the math is monotone-increasing in both modes.
  const sign = reversed ? -1 : 1;
  const v = value * sign;
  const ts: Array<[number, number]> = reversed ? targets.map(([s, t]) => [s, t * sign]) : targets;

  if (v <= ts[0][1]) return ts[0][0];
  const last = ts[ts.length - 1];
  if (v >= last[1]) return last[0];
  for (let i = 0; i < ts.length - 1; i++) {
    const [scoreLo, tLo] = ts[i];
    const [scoreHi, tHi] = ts[i + 1];
    if (v >= tLo && v <= tHi) {
      if (tHi === tLo) return scoreHi;
      const ratio = (v - tLo) / (tHi - tLo);
      return scoreLo + ratio * (scoreHi - scoreLo);
    }
  }
  return last[0];
}

function getStandards(movement: MovementRegistryEntry): BenchmarkStandards | null {
  const raw = movement.standards;
  if (!raw || typeof raw !== "object" || !raw.base || typeof raw.base !== "object") return null;
  return raw;
}

/**
 * Pure score from already-parsed inputs. Avoids re-parsing standards in
 * `buildIndicator`, which already has them.
 */
function scoreFromParsed(
  record: OneRepMax,
  baseEntries: Array<[number, number]>,
  multiplier: number,
  scoringType: MovementRegistryEntry["scoringType"]
): number | null {
  if (baseEntries.length === 0) return null;

  if (scoringType === SCORING_TYPE.BINARY) {
    return isUnlocked(record) ? baseEntries[0][0] : 0;
  }

  if (typeof record.value !== "number" || !Number.isFinite(record.value) || record.value <= 0) {
    return null;
  }

  const targets: Array<[number, number]> = baseEntries.map(([s, t]) => [s, t * multiplier]);
  // Post-LT-523: reverse interpolation is decided by the scoring SHAPE
  // (`lowerIsBetter` — Fran-style for-time), NOT the unit. A `time` +
  // `higherIsBetter` isometric hold now forward-interpolates (the bug fix).
  return interpolate(record.value, targets, scoringType === SCORING_TYPE.LOWER_IS_BETTER);
}

/**
 * Per-movement score 0-100 for the athlete. Mirrors `linearV1.js` exactly so
 * indicator scores match the trigger-computed axis aggregates.
 */
export function perMovementScore(
  record: OneRepMax,
  movement: MovementRegistryEntry,
  profile: Demographics
): number | null {
  const standards = getStandards(movement);
  if (!standards) return null;
  const baseEntries = toLevelEntries(standards.base);
  const multiplier =
    movement.scoringType === SCORING_TYPE.BINARY
      ? 1
      : combinedMultiplier(standards.modifiers, profile);
  return scoreFromParsed(record, baseEntries, multiplier, movement.scoringType);
}

/**
 * Returns the effective target (kg/reps/seconds) at a given score level.
 * The tier anchors in `base` are at scores 0/20/40/60/80/100, but the UI
 * needs milestones at 50/70 too — so we interpolate between adjacent anchors
 * the same way `interpolate` does in the score→target direction.
 */
function effectiveTargetAt(
  score: number,
  baseEntries: Array<[number, number]>,
  multiplier: number
): number | null {
  if (baseEntries.length === 0) return null;
  if (score <= baseEntries[0][0]) return baseEntries[0][1] * multiplier;
  const last = baseEntries[baseEntries.length - 1];
  if (score >= last[0]) return last[1] * multiplier;
  for (let i = 0; i < baseEntries.length - 1; i++) {
    const [scoreLo, tLo] = baseEntries[i];
    const [scoreHi, tHi] = baseEntries[i + 1];
    if (score >= scoreLo && score <= scoreHi) {
      const ratio = scoreHi === scoreLo ? 0 : (score - scoreLo) / (scoreHi - scoreLo);
      return (tLo + ratio * (tHi - tLo)) * multiplier;
    }
  }
  return null;
}

function buildIndicator(
  rec: OneRepMax,
  movement: MovementRegistryEntry,
  profile: Demographics
): AxisDetailIndicatorWithMeasure | null {
  const standards = getStandards(movement);
  if (!standards) return null;
  const baseEntries = toLevelEntries(standards.base);
  if (baseEntries.length === 0) return null;

  const isBinary = movement.scoringType === SCORING_TYPE.BINARY;
  // Don't render rows for binary movements the athlete hasn't unlocked yet —
  // every gymnastics axis would otherwise be flooded with locked rows.
  if (isBinary && !isUnlocked(rec)) return null;

  // Binary movements ignore demographic multipliers per SCORING-V1 §3.2.
  const multiplier = isBinary ? 1 : combinedMultiplier(standards.modifiers, profile);

  const score = scoreFromParsed(rec, baseEntries, multiplier, movement.scoringType);
  if (score === null) return null;

  // Binary movements have a single base anchor (e.g., `{ 60: 1 }`), so every
  // visible level resolves to the same target value via interpolation —
  // emitting them produces 4 markers stacked at left:100% plus 4 redundant
  // SR announcements. Skip milestone population entirely for binary; the
  // unlock celebration is the achievement, not progress through tiers.
  const milestones: Record<number, number> = {};
  if (!isBinary) {
    for (const lv of DEFAULT_VISIBLE_LEVELS) {
      const target = effectiveTargetAt(lv, baseEntries, multiplier);
      if (target !== null) milestones[lv] = target;
    }
  }

  // Ceiling = Lv100 effective target → progress bar spans 0→100-tier per AC3.
  // Falls back to the highest defined tier when Lv100 isn't seeded.
  const lv100Target =
    effectiveTargetAt(100, baseEntries, multiplier) ??
    baseEntries[baseEntries.length - 1][1] * multiplier;

  // For `lowerIsBetter` bars (Fran-style for-time), the visualization spans
  // Lv 0 (worst, leftmost — larger value) → Lv 100 (best, rightmost — smaller
  // value). The bar reads `floor` from `levelMilestones[0]` for this shape and
  // falls back to 0 for forward-scored bars. Without this entry, the bar would
  // set floor=0 and every Lv 50/60/70/80 marker would fall outside the
  // [0, targetValue] range and be dropped — bar shows zero markers and
  // visually lies. Driven by the scoring SHAPE post-LT-523, not the unit: a
  // `time` + `higherIsBetter` hold forward-scores like kg/reps and must NOT
  // get the descending floor.
  if (movement.scoringType === SCORING_TYPE.LOWER_IS_BETTER) {
    const lv0Target = effectiveTargetAt(0, baseEntries, multiplier);
    if (lv0Target !== null) milestones[0] = lv0Target;
  }

  const currentValue = isBinary ? (isUnlocked(rec) ? 1 : 0) : rec.value;
  const targetValue = isBinary ? 1 : lv100Target;

  return {
    movementID: movement.id,
    movementName: movement.name,
    currentValue,
    targetValue,
    currentLevel: Math.round(score),
    levelMilestones: milestones,
    scoringType: movement.scoringType,
    // `measureType` is the display unit. The published `AxisDetailIndicator`
    // only carries `scoringType` (the shape), so we attach the unit via the
    // local `AxisDetailIndicatorWithMeasure` extension (same pattern as
    // `MovementRegistryEntryWithMeta`) rather than churning the shared type.
    measureType: movement.measureType,
    // Required by the AxisDetailIndicator type; IndicatorProgressBar derives
    // its own fill from currentValue/targetValue and ignores this field.
    progressPercent: Math.max(0, Math.min(1, score / 100)),
  };
}

/**
 * Build an `AxisDetail` for the open axis from the data already streaming to
 * the dashboard. Returns null when the registry hasn't loaded yet — callers
 * should render a loading state in that case.
 */
export function computeAxisDetail(
  axis: RadarAxis,
  profile: AthleteRadarProfile | null,
  demographics: Demographics,
  /** One record per movement, keyed by movement id. */
  records: ReadonlyMap<string, OneRepMax>,
  registry: ReadonlyArray<MovementRegistryEntry>
): (Omit<AxisDetail, "indicators"> & { indicators: AxisDetailIndicatorWithMeasure[] }) | null {
  if (registry.length === 0) return null;

  const registryByID = new Map<string, MovementRegistryEntry>();
  for (const entry of registry) {
    if (entry?.id) registryByID.set(entry.id, entry);
  }

  const indicators: AxisDetailIndicatorWithMeasure[] = [];
  for (const [movementID, rec] of records) {
    const movement = registryByID.get(movementID);
    if (!movement || movement.radarAxis !== axis) continue;
    const indicator = buildIndicator(rec, movement, demographics);
    if (indicator) indicators.push(indicator);
  }

  // Deterministic order by name so list animations don't flicker on re-render.
  indicators.sort((a, b) => a.movementName.localeCompare(b.movementName));

  const score = Math.round(profile?.scores?.[axis] ?? 0);
  const isStrongest = profile?.strongestAxis === axis;
  const isWeakest = profile?.weakestAxis === axis;

  return {
    axis,
    axisDisplayName: RADAR_AXIS_DISPLAY_NAMES[axis],
    score,
    contextMessage: defaultContextMessage(axis, isWeakest, isStrongest),
    benchmarkLabel: computeBenchmarkLabel(demographics),
    indicators,
  };
}
